import React from 'react';
import config from 'src/shared/config';
import { T } from 'src/shared/helpers';
import { ServiceConfig } from './serviceCredits';

// Centralized configuration for every add-on rendered through the shared ServiceCredits component.
// Each factory keeps the add-on copy (name, description, guide link, unit labels) in a single place so
// the subscriptions page card and any standalone purchase dialog stay in sync, and shares the runtime
// availability logic (trial / Shopify test store / plan restrictions) instead of duplicating it per call site.

// Runtime context needed to decide whether purchasing for an add-on is currently allowed.
export interface AddonAvailabilityContext {
    // The seller is in a trial period.
    inTrial: boolean;
    // The current sales channel is Shopify.
    isShopify: boolean;
    // The Shopify store is a development/test store.
    isTestStore: boolean;
    // Extra, add-on specific blocking reason, evaluated after the test-store check and before the trial
    // check (e.g. the current plan version does not support the feature). Does not affect notAvailableInTrial.
    extraDisabled?: boolean;
    extraDisabledMessage?: string;
}

// The unit a given add-on is purchased in. Kept as distinct literal strings so existing translations stay intact.
type AddonUnit = 'credits' | 'testOrders';

// Shopify development/test stores cannot purchase while running in production.
const isShopifyTestStoreBlocked = (ctx: AddonAvailabilityContext, unit: AddonUnit): boolean =>
    ctx.isShopify && ctx.isTestStore && (config.isProduction || unit === 'testOrders');

const getTestStoreBlockedMessage = (unit: AddonUnit): string =>
    unit === 'testOrders'
        ? T._('Sorry! Your account is currently a development store, which means you can not purchase test orders.', 'Admin')
        : T._('Sorry! Your account is currently a development store, which means you can not purchase credits.', 'Admin');

const getTrialBlockedMessage = (unit: AddonUnit): string =>
    unit === 'testOrders'
        ? T._('Unable to purchase test orders during the trial period.', 'Admin')
        : T._('Unable to purchase credits during the trial period.', 'Admin');

// Builds the disabled / disabledMessage / notAvailableInTrial fields shared by every credit add-on.
// Message priority mirrors the original inline logic: test store > extra reason > trial.
const buildAddonAvailability = (ctx: AddonAvailabilityContext, unit: AddonUnit): Partial<ServiceConfig> => {
    const trialBlocked = ctx.inTrial && !ctx.isShopify;
    const testStoreBlocked = isShopifyTestStoreBlocked(ctx, unit);
    const baseBlocked = trialBlocked || testStoreBlocked;

    return {
        disabled: baseBlocked || (ctx.extraDisabled ?? false),
        notAvailableInTrial: baseBlocked,
        disabledMessage: testStoreBlocked
            ? getTestStoreBlockedMessage(unit)
            : ctx.extraDisabled
              ? ctx.extraDisabledMessage
              : ctx.inTrial
                ? getTrialBlockedMessage(unit)
                : undefined
    };
};

// Test Orders add-on: reused by the subscriptions page card and the standalone purchase dialog.
export const getTestOrdersServiceConfig = (availability: AddonAvailabilityContext, overrides?: Partial<ServiceConfig>): ServiceConfig => ({
    serviceName: 'TEST_ORDERS',
    addonKey: 'TEST_ORDERS',
    displayName: T._('Test Orders', 'Admin'),
    description: <>{T._('Purchase additional test orders to validate your customizable products and generate print-ready files.', 'Admin')}</>,
    learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/360018551700-How-to-test-a-customizable-product-and-place-a-test-order',
    addonCurrencyCode: 'USD',
    addonCtaLabel: T._('Buy', 'Admin'),
    addonPackageUnitLabel: T._('test orders', 'Admin'),
    addonPurchaseSuccessMessage: T._('Your test orders package has been purchased successfully.', 'Admin'),
    ...buildAddonAvailability(availability, 'testOrders'),
    ...overrides
});

// AI Creative Tools add-on (GENERATIVE_AI).
export const getGenerativeAiServiceConfig = (availability: AddonAvailabilityContext, overrides?: Partial<ServiceConfig>): ServiceConfig => ({
    serviceName: 'GENERATIVE_AI',
    addonKey: 'GENERATIVE_AI',
    displayName: T._('AI Creative tools', 'Admin'),
    description: <>{T._('Enable your customers with AI design tools.', 'Admin')}</>,
    learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/sections/23868451889180-AI-Creative-Tools',
    addonCurrencyCode: 'USD',
    ...buildAddonAvailability(availability, 'credits'),
    ...overrides
});

// AI Agents Studio add-on (AI_STUDIO).
export const getAiStudioServiceConfig = (availability: AddonAvailabilityContext, overrides?: Partial<ServiceConfig>): ServiceConfig => ({
    serviceName: 'AI_STUDIO',
    addonKey: 'AI_STUDIO',
    displayName: T._('AI Agents Studio', 'Admin'),
    description: <>{T._('Generate visuals, videos and 3D models with AI Agents Studio tools.', 'Admin')}</>,
    learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/25239933114908-AI-Agents-Studio',
    addonCurrencyCode: 'USD',
    ...buildAddonAvailability(availability, 'credits'),
    ...overrides
});

// AI Vectorizer add-on (VECTORIZE). Callers pass plan support via overrides (isToolSupportedByPlan) and the
// plan-version restriction through availability.extraDisabled / extraDisabledMessage.
export const getVectorizeServiceConfig = (availability: AddonAvailabilityContext, overrides?: Partial<ServiceConfig>): ServiceConfig => ({
    serviceName: 'VECTORIZE',
    addonKey: 'VECTORIZE',
    displayName: T._('AI Vectorizer', 'Admin'),
    description: <>{T._('Automatically converts raster images into vector files in the UI.', 'Admin')}</>,
    learnMoreUrl: 'https://zakeke.zendesk.com/hc/en-us/articles/22673500547996-Vectorizer',
    addonCurrencyCode: 'USD',
    ...buildAddonAvailability(availability, 'credits'),
    ...overrides
});
